import { api } from '@/axios/instance/api';
import { tokenInstance } from '@/axios/instance/token';
import { type InvitationLoginPayload, LOGIN_ROUTE, type LoginPayload, REFRESH_ROUTE, type UserProfile } from '@/types';

export const login = (payload: LoginPayload) => {
  return api.post<{ refresh: string; access: string }>(LOGIN_ROUTE, payload);
};

export const getUserProfile = () => {
  return api.get<UserProfile>('/users/profile/');
};

export const logout = () => {
  return api.post<void>('/users/logout_request/');
};

export const refreshToken = (payload: { refresh: string }) => {
  return tokenInstance.post<{ refresh: string; access: string }>(REFRESH_ROUTE, payload);
};

export const verifyCredentials = (payload: LoginPayload) => {
  return api.post<{ detail?: string }>('/users/credentials_verification/', payload);
};

export const fetchUserTenant = (tenant: string) =>
  api.get<{ data?: string; error?: string }>('signup/user-tenant', { params: { name: tenant } });

export const fetchUserTenantLogin = (email: string) => api.get<void>('signup/find-tenant-login', { params: { email } });

export const resetPassword = (email: string) => api.post<{ detail?: string }>('/password_reset/', { email });

export const tokenValidate = (payload: { token: string }) => {
  return api.post<{ detail?: string }>('/password_reset/validate_token/', payload);
};

export const passwordReset = (payload: { token: string; password: string }) => {
  return api.post<{ password?: string[]; detail?: string }>('/password_reset/confirm/', payload);
};

export const verifyMfaToken = (payload: { token: string }, corelationId: string | null) => {
  return tokenInstance.post<{ access_token: string; refresh_token: string }>(
    '/users/mfa_token_verification/',
    payload,
    {
      headers: {
        'X-Correlation-ID': corelationId,
      },
    }
  );
};

export const resendMfaToken = (payload: null, corelationId: string | null) => {
  return tokenInstance.post<void>('/users/resend_mfa_token/', payload, {
    headers: {
      'X-Correlation-ID': corelationId,
    },
  });
};

export const getInvitations = (invitationId: string) => {
  return api.get<{ error?: string; detail: string; active: boolean; domain: string; user: UserProfile }>(
    `/users/invitations/${invitationId}`
  );
};

export const invitationLogin = (invitationId: string, payload: InvitationLoginPayload) => {
  return api.post<{ password1?: string[]; password2?: string[]; access_token?: string; refresh_token?: string }>(
    `/users/invitations/${invitationId}/login`,
    payload
  );
};

export const updateProfileImage = async (id: number, image: File) => {
  const formData = new FormData();
  formData.append('avatar', image);

  return api.patch(`/users/${id}/update-avatar`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteProfileImage = async (id: number) => {
  return api.delete(`/users/${id}/delete-avatar`);
};

export const deleteSelf = async (payload: { email: string; password: string }) => {
  return api.post(`/users/delete_self`, payload);
};

export const patchUser = async (
  id: number,
  data: { first_name: string; last_name: string; email: string; group: string }
) => {
  return await api.patch(`/users/${id}`, data);
};

export const passwordChange = (password: string) => {
  return api.post('/users/password_change/', { new_password: password });
};
